import { BasePointDragger } from "./BasePointDragger";
import { DraggerType, type Point3D, type TypedInputMode } from "./types";
import {
  RELATIVE_POINT_MODES,
  relativePointValues,
  pointFromRelativeInput,
} from "./relativePointInput";
import {
  translationTransform,
  type EntityTransform,
} from "../transform/entityTransform";

/**
 * Interactive "move from base point" tool — AutoCAD MOVE. Pick a base point,
 * then a destination point; the selected entities are translated by the vector
 * between them and the command ends.
 */
export class MoveDragger extends BasePointDragger {
  readonly type = DraggerType.Move;

  protected readonly followUpPointCount = 1;

  protected computeTransform(current: Point3D): EntityTransform {
    const base = this.basePoint;
    if (!base) return translationTransform(0, 0);
    return translationTransform(current.x - base.x, current.y - base.y);
  }

  protected commit(transform: EntityTransform): void {
    this.commitTransformInPlace(transform);
  }

  protected promptKey(): string {
    return this.basePoint
      ? "canvas:selection.basePointPrompt.destination"
      : "canvas:selection.basePointPrompt.base";
  }

  // ====== Typed input: the destination, as an X/Y or length/angle offset ======

  protected override typedInputModes(): TypedInputMode[] | null {
    return this.basePoint ? RELATIVE_POINT_MODES : null;
  }

  protected override typedInputTitleKey(): string {
    return "canvas:tools.options.keypad.title.move";
  }

  protected override typedInputValues(
    current: Point3D | null
  ): Record<string, number> {
    return relativePointValues(this.basePoint, current);
  }

  protected override worldPointFromTypedValues(
    modeId: string,
    values: Record<string, number>
  ): Point3D | null {
    return pointFromRelativeInput(this.basePoint, modeId, values);
  }
}
